/* A value you read out loud to somebody on a support line.

   Account numbers, support numbers, circuit references. §6 calls the account
   number "the field you need at 3am and cannot find", and having found it the
   next thing you do is read it into a phone or paste it into a portal. So it is
   monospace — digit groups are much harder to lose your place in — and one
   click copies it with a confirmation you can see without looking away.

   clipboard.writeText needs a secure context. The app is HTTPS everywhere it is
   deployed, but a copy that silently does nothing would be worse than no button
   at all, so failure says so rather than showing a tick. */

import { useEffect, useRef, useState } from 'react';
import s from './screens.module.scss';

export function Copyable({ value, large = false }: { value: string; large?: boolean }) {
  const [state, setState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  const copy = async (e: React.MouseEvent) => {
    /* The table row underneath opens the drawer. Copying a number is not a
       request to open anything. */
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setState('copied');
    } catch {
      setState('failed');
    }
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setState('idle'), 1800);
  };

  return (
    <button
      type="button"
      className={`${s.copyable} ${large ? s.copyableLarge : ''}`}
      onClick={copy}
      title={state === 'failed' ? 'Could not copy — select it by hand' : `Copy ${value}`}
      aria-label={`Copy ${value}`}
    >
      <span className={s.mono}>{value}</span>
      <i
        className={`ti ${
          state === 'copied' ? 'ti-check' : state === 'failed' ? 'ti-alert-triangle' : 'ti-copy'
        }`}
        aria-hidden
      />
      <span className={s.copyState} aria-live="polite">
        {state === 'copied' ? 'Copied' : state === 'failed' ? 'Could not copy' : ''}
      </span>
    </button>
  );
}
